import { Box, Collapse, Divider, Grid, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { FormStatusSelect } from './FormStatusSelect';
import { FormTypeBadge } from './FormTypeBadge';
import type { FormSubmissionTableItem } from './FormsTable';

interface FormTableRowProps {
    data: FormSubmissionTableItem;
    onStatusUpdated: () => void;
}

const TRUNCATED_CELL_SX = {
    maxWidth: 220,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
} as const;

const FormTableCellText = ({ value }: { value: string }): JSX.Element => (
    <Tooltip title={value === '-' ? '' : value}>
        <Typography variant="body2" sx={TRUNCATED_CELL_SX}>
            {value}
        </Typography>
    </Tooltip>
);

const DetailItem = ({ label, value }: { label: string; value: string }): JSX.Element => (
    <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {value}
        </Typography>
    </Box>
);

export const FormTableRow = ({ data, onStatusUpdated }: FormTableRowProps): JSX.Element => {
    const [isRowOpen, setIsRowOpen] = useState(false);

    return (
        <>
            <TableRow
                hover
                tabIndex={-1}
                onClick={() => setIsRowOpen((currentValue) => !currentValue)}
                sx={{
                    '&:nth-of-type(even)': {
                        backgroundColor: 'background.neutral'
                    },
                    '&:hover': {
                        backgroundColor: 'action.hover'
                    },
                    cursor: 'pointer'
                }}
            >
                <TableCell>
                    <FormTypeBadge formType={data.formType} />
                </TableCell>
                <TableCell>
                    <FormTableCellText value={data.restaurantName} />
                </TableCell>
                <TableCell>
                    <FormTableCellText value={data.contactName} />
                </TableCell>
                <TableCell>
                    <FormTableCellText value={data.city} />
                </TableCell>
                <TableCell sx={{ minWidth: 180 }} onClick={(event) => event.stopPropagation()}>
                    <FormStatusSelect submissionId={data.id} status={data.status} onUpdated={onStatusUpdated} />
                </TableCell>
                <TableCell>
                    <FormTableCellText value={data.createdAtDisplay} />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={isRowOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Business Details
                                    </Typography>
                                    <Box sx={{ display: 'grid', gap: 1 }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                                                Enquiry Type
                                            </Typography>
                                            <FormTypeBadge formType={data.formType} />
                                        </Box>
                                        <DetailItem label="Business Type" value={data.businessType} />
                                        <DetailItem label="City" value={data.city} />
                                        <DetailItem label="Referral Source" value={data.referralSource} />
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Contact Details
                                    </Typography>
                                    <Box sx={{ display: 'grid', gap: 1 }}>
                                        <DetailItem label="Contact Person" value={data.contactName} />
                                        <DetailItem label="Mobile" value={data.mobileNumber} />
                                        <DetailItem label="Email" value={data.email} />
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Timeline
                                    </Typography>
                                    <Box sx={{ display: 'grid', gap: 1 }}>
                                        <DetailItem label="Created At" value={data.createdAtDisplay} />
                                        <DetailItem label="Updated At" value={data.updatedAtDisplay} />
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Divider />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Requirement Notes
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {data.message}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};
